<template>
  <modal class="tag-dictionary-portal" :name="modalName" width="90%" height="auto" :adaptive="false" :scrollable="false"
    :click-to-close="true">
    <div class="tag-dictionary__content">
      <Button class="close" @click.native="$modal.hide(modalName)" margin="0 0 8px auto" color="#31b9be"
        bgcolor="transparent" width="30px" padding="0" icon="close-blue"></Button>

      <h3 class="tag-dictionary__title">#{{ activeTagLabel }}</h3>

      <p v-if="isLoading" class="tag-dictionary__state">
        {{ $t('states.loading') }}
      </p>
      <p v-else-if="errorMessage" class="tag-dictionary__state tag-dictionary__state--error">
        {{ errorMessage }}
      </p>
      <p v-else-if="entries.length === 0" class="tag-dictionary__state">
        {{ $t('app.dictionary.not-found') }}
      </p>

      <ul v-else class="tag-dictionary__list">
        <li v-for="entry in entries" :key="entry.id" class="tag-dictionary__item">
          <button type="button" class="tag-dictionary__entry-toggle" @click="toggleEntry(entry.id)">
            <span class="tag-dictionary__entry-title">{{ getEntryTitle(entry) }}</span>

            <span class="tag-dictionary__arrow-wrap" :class="{ 'tag-dictionary__arrow-wrap--open': isOpen(entry.id) }">
              <img :src="require('@/assets/icons/arrow-down-pink.svg')" class="tag-dictionary__entry-arrow" alt=""
                aria-hidden="true" />
            </span>
          </button>

          <div v-if="isOpen(entry.id)" class="tag-dictionary__entry-body">
            <div class="tag-dictionary__entry-body-inner">
              <p class="tag-dictionary__entry-content">
                {{ getEntryDescription(entry) }}
              </p>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </modal>
</template>

<script>
import { neoparentDictionaryEntries } from "@/api";
import Button from "@/page_elements/Button";

export default {
  name: "TagDictionaryEntriesModal",

  components: {
    Button
  },

  props: {
    postId: { required: true }
  },
  data() {
    return {
      isLoading: false,
      errorMessage: "",
      entries: [],
      activeTagId: null,
      activeTagLabel: "",
      expandedEntries: {}
    };
  },

  computed: {
    modalName() {
      return `tag-dictionary-modal-${this.postId}`;
    }
  },

  methods: {

    async openForTag(tag) {
      if (!tag) return;

      this.activeTagId = tag.id;
      this.activeTagLabel = tag.displayName || tag.name || "";

      this.entries = [];
      this.errorMessage = "";
      this.expandedEntries = {};
      this.isLoading = true;

      this.$modal.show(this.modalName);

      try {

        const response = await neoparentDictionaryEntries.getAll("", false, false);
        const data = response?.data?.entries || response?.data || [];

        this.entries = data.filter(entry =>
          entry.tags?.some(t => t.id === this.activeTagId)
        );

      } catch (e) {
        this.errorMessage = "Failed to load entries.";
      }

      this.isLoading = false;
    },

    getEntryTitle(entry) {
      if (!entry) return "";
      return entry.title || "Untitled entry";
    },

    getEntryDescription(entry) {
      if (!entry) return "";
      return (entry.description || entry.content || "").trim();
    },

    toggleEntry(id) {
      this.$set(this.expandedEntries, id, !this.expandedEntries[id]);
    },

    isOpen(id) {
      return !!this.expandedEntries[id];
    }

  }
};
</script>

<style>
.tag-dictionary-portal .vm--modal {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  max-height: 85vh !important;
  max-width: 560px !important;
  overflow: hidden !important;
  border-radius: 16px !important;
}

@media (max-width: 768px) {
  .tag-dictionary-portal .vm--modal {
    /* Override both width and max-width so inline styles from vue-js-modal cannot win */
    width: 92vw !important;
    max-width: 92vw !important;
    max-height: 88vh !important;
  }
}
</style>

<style scoped lang="scss">
.tag-dictionary__content {
  padding: 18px 18px 16px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
}

.tag-dictionary__title {
  flex: 0 0 auto;
  margin: 0 0 14px;
  color: #00a9b3;
  word-break: break-word;
}

.tag-dictionary__state {
  flex: 0 0 auto;
  color: #66727a;
  margin: 8px 0 6px;
}

.tag-dictionary__state--error {
  color: #cf4a72;
}

.tag-dictionary__list {
  flex: 1 1 auto;
  min-height: 0;
  list-style: none !important;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}

.tag-dictionary__item {
  list-style: none !important;
  display: block !important;
  border: 1px solid #cdebe6;
  border-radius: 12px;
  overflow: hidden;
}

.tag-dictionary__item+.tag-dictionary__item {
  margin-top: 8px;
}

.tag-dictionary__entry-toggle {
  width: 100%;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 11px 12px 11px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  text-align: left;
  user-select: none;
  -webkit-user-select: none;

  &:hover,
  &:focus-visible {
    background: #f2fbfc;
    outline: none;
  }
}

.tag-dictionary__entry-title {
  flex: 1 1 0;
  min-width: 0;
  font-weight: 600;
  color: #24424a;
  line-height: 1.35;
  word-break: break-word;
  user-select: text;
  -webkit-user-select: text;
}

.tag-dictionary__arrow-wrap {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  -webkit-user-select: none;
  pointer-events: none;

  &--open {
    transform: rotate(180deg);
  }
}

.tag-dictionary__entry-arrow {
  width: 36px;
  height: 36px;
  display: block;
  pointer-events: none;
}

.tag-dictionary__entry-body {
  overflow: hidden;
}

.tag-dictionary__entry-body-inner {
  border-top: 1px solid #e4f3f0;
  padding: 10px 14px 13px;
}

.tag-dictionary__entry-content {
  margin: 0;
  color: #4d5c63;
  line-height: 1.5;
  white-space: pre-line;
  user-select: text;
  -webkit-user-select: text;
}

@media (max-width: 768px) {
  .tag-dictionary__content {
    padding: 14px 12px 12px;
    max-height: 88vh;
  }

  .tag-dictionary__title {
    margin-bottom: 10px;
    line-height: 1.25;
  }

  .tag-dictionary__entry-toggle {
    padding: 12px 10px 12px 12px;
  }

  .tag-dictionary__entry-title {
    font-size: 15px;
    line-height: 1.4;
  }

  .tag-dictionary__arrow-wrap {
    width: 34px;
    height: 34px;
  }

  .tag-dictionary__entry-arrow {
    width: 30px;
    height: 30px;
  }

  .tag-dictionary__entry-content {
    font-size: 14px;
    line-height: 1.6;
  }
}
</style>